/**
 * Stock background proxy — ephemeral search + download tracking.
 * Endpoints: GET /api/stock-backgrounds/search, POST /api/stock-backgrounds/track-download
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";

/**
 * @param {string} token
 * @param {{ query: string, page?: number, per_page?: number, orientation?: string }} params
 */
export async function unsplashSearch(token, params) {
  if (isMockApi) {
    return {
      success: true,
      images: [],
      total: 0,
      page: 1,
      per_page: params.per_page ?? 24,
      query: params.query || "",
      note: "mock",
    };
  }

  const sp = new URLSearchParams({
    query: params.query || "",
    page: String(params.page ?? 1),
    per_page: String(params.per_page ?? 24),
    orientation: params.orientation || "landscape",
  });

  const response = await apiClient.get(`/api/stock-backgrounds/search?${sp}`, {
    ...createAuthConfig(token),
    timeout: 60000,
  });
  return response.data;
}

/**
 * Track stock-provider download when a background is applied.
 * Curated: { external_asset_id }
 * Search: { download_location, provider_asset_id?, image_url? }
 *
 * Always calls our backend (source of truth for Unsplash cooldown).
 * Rate-limit → soft { deferred: true } (no throw); image already applied.
 *
 * @param {string} token
 * @param {{
 *   external_asset_id?: number,
 *   download_location?: string,
 *   provider_asset_id?: string,
 *   image_url?: string,
 * }} body
 */
export async function unsplashTrackDownload(token, body) {
  if (isMockApi) {
    return {
      success: true,
      tracked: true,
      tracked_via: body.external_asset_id ? "curated" : "search",
      image_url: body.image_url || null,
    };
  }

  try {
    const response = await apiClient.post(
      "/api/stock-backgrounds/track-download",
      body,
      { ...createAuthConfig(token), timeout: 30000 }
    );
    return response.data || {};
  } catch (err) {
    const status = err?.response?.status;
    const detail = String(err?.response?.data?.detail || err?.message || "");
    const rateLimited =
      status === 429 ||
      status === 403 ||
      /rate limit/i.test(detail);
    if (rateLimited) {
      return {
        success: true,
        tracked: false,
        deferred: true,
        detail: "rate_limited",
        retry_after: Number(err?.response?.data?.retry_after) || 120,
        image_url: body.image_url || null,
      };
    }
    throw err;
  }
}

export default {
  unsplashSearch,
  unsplashTrackDownload,
};
